import {useCallback, useState} from "react";
import {DocumentNode} from "graphql";
import {useMutation} from "@apollo/client";
import {useFlags} from "launchdarkly-react-client-sdk";
import {type GraphQLQuery} from "@aws-amplify/api-graphql";
import {features} from "../../features";
import {useAmplifyClients} from "../AmplifyClientProvider";
import {
  AmplifyClientKey,
  GraphqlResult,
  GraphqlVariables,
  MutationTrigger,
  toError
} from "./types";

interface UseGraphqlMutationOptions<
  TData,
  TVariables extends GraphqlVariables
> {
  /** Apollo DocumentNode — required during migration, removed after Apollo cleanup */
  document: DocumentNode;
  /** Amplify mutation string */
  amplifyMutation: string;
  /** Which Amplify client to use (defaults to "appsyncBff") */
  amplifyClient?: AmplifyClientKey;
  /** Apollo context (e.g., { endpoint: Endpoints.APPSYNC }) */
  context?: Record<string, unknown>;
  /** fetchPolicy */
  fetchPolicy?: "network-only" | "no-cache";
}

/**
 * Framework-agnostic mutation hook returning [mutateFn, { data, loading, error }].
 * Routes to Apollo or Amplify based on the migration feature flag.
 */
export function useGraphqlMutation<
  TData = unknown,
  TVariables extends GraphqlVariables = GraphqlVariables
>(
  options: UseGraphqlMutationOptions<TData, TVariables>
): [MutationTrigger<TData, TVariables>, GraphqlResult<TData>] {
  const flags = useFlags();
  const useAmplify =
    flags[features.dev.amplifyClient.useAmplifyForAllOperations];

  // === Apollo path ===
  const [apolloMutate, apolloResult] = useMutation<TData, TVariables>(
    options.document,
    {
      context: options.context,
      fetchPolicy: options.fetchPolicy
    }
  );

  // === Amplify path ===
  const clients = useAmplifyClients();
  const client = clients?.[options.amplifyClient ?? "appsyncBff"] ?? null;
  const [amplifyData, setAmplifyData] = useState<TData | undefined>(undefined);
  const [amplifyLoading, setAmplifyLoading] = useState(false);
  const [amplifyError, setAmplifyError] = useState<Error | undefined>(
    undefined
  );

  const amplifyMutate: MutationTrigger<TData, TVariables> = useCallback(
    async (mutateOptions?) => {
      const vars = mutateOptions?.variables;
      setAmplifyLoading(true);
      setAmplifyError(undefined);

      try {
        if (!client) {
          throw new Error("Amplify client not initialized");
        }

        const result = await client.graphql<GraphQLQuery<TData>>({
          query: options.amplifyMutation,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          variables: vars as any
        });
        const data = result.data as TData;

        setAmplifyData(data);
        setAmplifyLoading(false);
        return {data};
      } catch (err) {
        const error = toError(err);
        setAmplifyError(error);
        setAmplifyLoading(false);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, options.amplifyMutation]
  );

  // Wrap Apollo trigger to match our interface
  const apolloMutateWrapper: MutationTrigger<TData, TVariables> = useCallback(
    async (mutateOptions?) => {
      const result = await apolloMutate({
        variables: mutateOptions?.variables as TVariables
      });
      return {data: result.data ?? undefined};
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apolloMutate]
  );

  if (useAmplify) {
    return [
      amplifyMutate,
      {data: amplifyData, loading: amplifyLoading, error: amplifyError}
    ];
  }

  return [
    apolloMutateWrapper,
    {
      data: apolloResult.data ?? undefined,
      loading: apolloResult.loading,
      error: apolloResult.error
    }
  ];
}
